import { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useInView } from 'react-intersection-observer';

import { useOverlayState } from 'components/Overlay';
import { getPixleeContentFromAlbum, getPixleeContentFromSku } from 'features/pixlee/api';
import { PixleeWidgetEvent, UGCEvent, ArtificialPageEvent } from 'features/pixlee/events';
import utagLinkGeneric from 'features/tealium/utagLinkGeneric';
import { setGenericEvent } from 'features/tealium/ga4/custom';

const isMobile = window.utag_data && window.utag_data.is_mobile;
const { page_type, product_category: productCategory = [] } = window.utag_data;
const [ primaryCategory ] = productCategory;

function PixleeWidget ({
	children,
	albumDetails,
	UGCEventTypes,
	gaEvents,
	remainingItemsToSlide,
	productSku = null,
	popinPageName = '',
	onLoadCallback,
	sectionBasedWidget = null,
	slidesToShowSectionOnWidget = 6,
	activeImageQuerySelector = '.react-multi-carousel-item--active button'
}) {

	const [ inViewRef, inView ] = useInView({
		triggerOnce: true
	});

	const [ list, updateList ] = useState({});
	const [ listForSectionBasedWidget, updatelistForSectionBasedWidget ] = useState({});
	const [ activeSlideIndex, updateActiveSlideIndex ] = useState(0);
	const [ initialModalSlideIndex, updateInitialModalSlideIndex ] = useState(0);
	const modal = useOverlayState();

	const { showOverlay, show } = modal;

	if (isMobile === '1') {

		if (show) {

			document.body.style.overflow = 'hidden';

		} else {

			document.body.style.overflow = '';

		}

	}

	const updateModalProps =		(item, index) => ({ shopNowButtonClicked }) => {

		updateInitialModalSlideIndex(index);
		showOverlay();
		PixleeWidgetEvent({
			type: 'opened:lightbox',
			data: {
				album_id: item.album_id,
				album_photo_id: item.album_photo_id
			}
		});
		if (!!shopNowButtonClicked && UGCEventTypes.shopNowButtonAction) {

			UGCEvent({
				eventAction: UGCEventTypes.shopNowButtonAction
			});

		} else {

			UGCEvent({
				eventAction: UGCEventTypes.pageAction,
				eventLabel: index + 1
			});

		}

		ArtificialPageEvent({ pageName: popinPageName });

		if (gaEvents && gaEvents.click) {

			const { eventCategory, eventAction, eventLabel, productSku, ga4EventName } = gaEvents.click;
			utagLinkGeneric({
				eventCategory,
				eventAction,
				eventLabel: eventLabel(item, index),
				nonInteraction: 0
			});
			if(ga4EventName) {

				setGenericEvent({
					event_name: ga4EventName,
					pdp_item_id: productSku,
					pdp_prime_cat: primaryCategory,
					item_position_number:index + 1
				});
			}
		}

	};

	useEffect(() => {

		const responseHandler = (response) => {

			response.isEmpty = undefined;

			if (response && response.data && response.data.length) {

				response.isEmpty = false;
				updateList(response);
				if (sectionBasedWidget) {

					updatelistForSectionBasedWidget({
						data: new Array(Math.ceil(response.data.length / slidesToShowSectionOnWidget))
							.fill()
							.map((_) => response.data.splice(0, slidesToShowSectionOnWidget))
					});

				}
				if (typeof onLoadCallback === 'function') {

					onLoadCallback(true); // pass true to indicate there is data

				}

				// this function should execute after https://assets.pixlee.com/assets/pixlee_events.js
				// and https://www.google-analytics.com/analytics.js
				setTimeout(() => {

					PixleeWidgetEvent({
						type: 'opened:widget',
						data: {
							album_id: response.album_id,
							widget: 'Horizontal'
						}
					});

					UGCEvent({
						eventAction: UGCEventTypes.loadPage,
						eventLabel: '',
						nonInteraction: 1
					});

				}, 2000);

				if (gaEvents && gaEvents.load) {

					const { eventCategory, eventAction, eventLabel, productSku, ga4EventName } = gaEvents.load;
					utagLinkGeneric({
						eventCategory,
						eventAction,
						eventLabel: eventLabel(),
						nonInteraction: 1
					});
					if(ga4EventName) {
						setGenericEvent({
							event_name: ga4EventName,
							pdp_item_id: productSku,
							pdp_prime_cat: primaryCategory,
						});

					}

				}

				const widget = document.querySelectorAll('.pixleeContainer');

				widget.forEach((pixlee) => {

					pixlee.classList.remove('hidden');

				});

			} else {

				if (typeof onLoadCallback === 'function') {

					onLoadCallback();

				}
				response.isEmpty = true;
				updateList(response);
				if (sectionBasedWidget) {

					updatelistForSectionBasedWidget({
						data: new Array(Math.ceil(response.data.length / slidesToShowSectionOnWidget))
							.fill()
							.map((_) => response.data.splice(0, slidesToShowSectionOnWidget))
					});

				}

			}

		};

		if (productSku) {

			getPixleeContentFromSku({ ...albumDetails, productSku }).then(responseHandler);

		} else {

			getPixleeContentFromAlbum(albumDetails).then(responseHandler);

		}

	}, []);

	useEffect(() => {

		if (inView) {

			PixleeWidgetEvent({
				type: 'widget:visible',
				data: {
					album_id: list.album_id,
					widget: 'Horizontal'
				}
			});

		}

	}, [ inView ]);

	const beforeChange = (nextSlide, { currentSlide, totalItems }) => {

		updateActiveSlideIndex(nextSlide);

		const loadMore = totalItems - remainingItemsToSlide <= currentSlide;

		if (
			(list && list.next && loadMore && nextSlide > currentSlide)
			|| (sectionBasedWidget
				&& listForSectionBasedWidget
				&& listForSectionBasedWidget.next
				&& loadMore
				&& nextSlide > currentSlide)
		) {

			const responseHandler = (response) => {

				if (response && response.data && response.data.length) {

					const moreData = list.data.concat(response.data);
					if (sectionBasedWidget) {

						const moreDataForHomeWidget = listForSectionBasedWidget.data.concat(
							new Array(Math.ceil(response.data.length / slidesToShowSectionOnWidget))
								.fill()
								.map((_) => response.data.splice(0, slidesToShowSectionOnWidget))
						);

						updatelistForSectionBasedWidget({
							...listForSectionBasedWidget,
							data: moreDataForHomeWidget,
							page: response.page,
							next: response.next
						});

					}

					updateList({
						...list,
						data: moreData,
						page: response.page,
						next: response.next
					});

				}

			};

			if (productSku) {

				getPixleeContentFromSku({ ...albumDetails, page: list.page + 1, productSku }).then(responseHandler);

			} else {

				getPixleeContentFromAlbum({ ...albumDetails, page: list.page + 1 }).then(responseHandler);

			}

		}

	};

	const afterChange = (previousSlide, { currentSlide }) => {

		if (previousSlide < currentSlide) {

			// Right arrow click OR right swipe
			const activeImage = document.querySelector(activeImageQuerySelector);
			if (activeImage) {

				activeImage.classList.add('onlyFocus');
				activeImage.focus();

			}

			PixleeWidgetEvent({
				type: 'nav:right',
				data: {
					album_id: list.album_id
				}
			});
			UGCEvent({
				eventAction: UGCEventTypes.carouselForwardArrowClick
			});
			if(UGCEventTypes.ga4CarouselForwardArrowClick && page_type === "product") {
				setGenericEvent({
					event_name: UGCEventTypes.ga4CarouselForwardArrowClick,
					pdp_item_id: productSku,
					pdp_prime_cat: primaryCategory,
				});
			}

		} else if (previousSlide > currentSlide) {

			// Left arrow click OR left swipe
			const activeImages = document.querySelectorAll(activeImageQuerySelector);
			if (activeImages.length) {

				activeImages[activeImages.length - 1].classList.add('onlyFocus');
				activeImages[activeImages.length - 1].focus();

			}

			UGCEvent({
				eventAction: UGCEventTypes.carouselBackArrowClick
			});
			if (UGCEventTypes.ga4CarouselBackArrowClick && page_type === 'product') {
				setGenericEvent({
					event_name: UGCEventTypes.ga4CarouselBackArrowClick,
					pdp_item_id: productSku,
					pdp_prime_cat: primaryCategory,
				});
			}

		}

	};

	return children(
		updateModalProps,
		inViewRef,
		activeSlideIndex,
		list,
		beforeChange,
		modal,
		show,
		afterChange,
		initialModalSlideIndex,
		listForSectionBasedWidget
	);

}

PixleeWidget.propTypes = {
	albumDetails: PropTypes.shape({
		albumId: PropTypes.number,
		page: PropTypes.number,
		filters: PropTypes.string,
		sort: PropTypes.string
	}).isRequired,
	productSku: PropTypes.string,
	UGCEventTypes: PropTypes.objectOf(PropTypes.string).isRequired,
	remainingItemsToSlide: PropTypes.number.isRequired,
	gaEvents: PropTypes.shape({
		click: PropTypes.shape({
			eventCategory: PropTypes.string,
			eventAction: PropTypes.string,
			EventLabel: PropTypes.func
		}),
		load: PropTypes.shape({
			eventCategory: PropTypes.string,
			eventAction: PropTypes.string,
			EventLabel: PropTypes.func
		})
	}),
	children: PropTypes.func.isRequired,
	popinPageName: PropTypes.string,
	sectionBasedWidget: PropTypes.number,
	slidesToShowSectionOnWidget: PropTypes.number,
	onLoadCallback: PropTypes.func
};

export default PixleeWidget;
